import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from 'react-router';
import { CostingType, type Product } from '../../models/Product';
import { ChangeEvent, SubmitEvent, useCallback, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import { Autocomplete, FormControlLabel, FormHelperText, FormLabel, IconButton, MenuItem, Switch } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { red } from '../../theme/themePrimitives';
import ProductItemForm, { ProductItemFormState } from '../productItems/ProductItemForm';
import type { ProductItem } from '../../models/ProductItem';
import { useQuery } from '@apollo/client/react';
import { GET_ITEMS_QUERY } from '../../queries/ItemQueries';
import { GET_PRODUCTS_QUERY } from '../../queries/ProductQueries';
import { ProductBulkPricingMarkupLineItem } from '../../models/ProductBulkPricingMarkupLineItem';
import ProductBulkPricingMarkupLineItemForm, { ProductBulkPricingMarkupLineItemFormState } from '../productBulkPricingMarkupLineItems/ProductBulkPricingMarkupLineItemForm';
import { GET_CUSTOMERS_QUERY } from '../../queries/CustomerQueries';
import { Customer } from '../../models/Customer';

export interface ProductFormState {
  values: Partial<Omit<Product, 'id'>>;
  errors: Partial<Record<keyof ProductFormState['values'], string>>;
}

export interface ProductFormProps {
  formState: ProductFormState;
  itemErrors?: Map<number, Partial<Record<keyof ProductItemFormState['values'], string>>>;
  bulkPricingMarkupLineItemErrors?: Map<number, Partial<Record<keyof ProductBulkPricingMarkupLineItemFormState['values'], string>>>;
  onFieldChange: (
    name: keyof ProductFormState['values'],
    value: FormFieldValue,
    index?: number
  ) => void;
  onItemFieldChange: (items: ProductItem[]) => void;
  onBulkPricingMarkupLineItemFieldChange: (bulkPricingMarkupLineItems: ProductBulkPricingMarkupLineItem[]) => void;
  onSubmit: (formValues: Partial<ProductFormState['values']>, items: ProductItem[], bulkPricingMarkupLineItems: ProductBulkPricingMarkupLineItem[]) => Promise<void>;
  onReset?: (formValues: Partial<ProductFormState['values']>) => void;
  submitButtonLabel: string;
  backButtonPath?: string;
  existingProductId?: number;
}

export default function ProductForm(props: ProductFormProps) {
  const {
    formState,
    itemErrors = new Map<number, Partial<Record<keyof ProductItemFormState['values'], string>>>(),
    bulkPricingMarkupLineItemErrors = new Map<number, Partial<Record<keyof ProductBulkPricingMarkupLineItemFormState['values'], string>>>(),
    onFieldChange,
    onItemFieldChange,
    onBulkPricingMarkupLineItemFieldChange,
    onSubmit,
    onReset,
    submitButtonLabel,
    backButtonPath,
    existingProductId
  } = props;

  const formValues = formState.values;
  const formErrors = formState.errors;

  const navigate = useNavigate();

  const { data: itemsData } = useQuery(
    GET_ITEMS_QUERY, {
    variables: {
      filter: {
        isIngredient: { eq: true }
      }
    }
  });

  const { data: customersData } = useQuery(
    GET_CUSTOMERS_QUERY);

  const { data: productsData } = useQuery(
    GET_PRODUCTS_QUERY, {
    variables: {
      filter: {
        id: { neq: existingProductId }
      }
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [productItems, setProductItems] = useState<ProductItem[]>(formValues.items || []);
  const [bulkPricingMarkupLineItems, setBulkPricingMarkupLineItems] = useState<ProductBulkPricingMarkupLineItem[]>(formValues.bulkPricingMarkupLineItems || []);

  const handleSubmit = useCallback(
    async (event: SubmitEvent) => {
      event.preventDefault();

      setIsSubmitting(true);

      try {
        await onSubmit(
          formValues,
          productItems,
          bulkPricingMarkupLineItems);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit, formValues, productItems, bulkPricingMarkupLineItems],
  );

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof ProductFormState['values'],
        event.target.value,
      );
    },
    [onFieldChange],
  );

  const handleCheckboxFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof ProductFormState['values'],
        event.target.checked
      );
    },
    [onFieldChange],
  );

  const handleFormFieldChange = useCallback(
    (name: string, value: number) => {
      onFieldChange(
        name as keyof ProductFormState['values'],
        value,
      );
    },
    [onFieldChange],
  );

  const handleMatchingProductFieldChange = useCallback(
    (matchingProductId: number) => {
      onFieldChange(
        'matchingProductId',
        matchingProductId
      );
    },
    [onFieldChange],
  );

  const handleItemFormFieldChange = useCallback(
    (updatedItem: Partial<ProductItemFormState['values']>, index: number) => {
      const updatedItems = [...productItems];

      updatedItems[index] = { ...productItems[index], ...updatedItem };

      setProductItems(updatedItems);

      onItemFieldChange(updatedItems);
    },
    [onItemFieldChange, productItems],
  );

  const handleBulkPricingMarkupLineItemFormFieldChange = useCallback(
    (updatedItem: Partial<ProductBulkPricingMarkupLineItemFormState['values']>, index: number) => {
      const updatedBulkPricingMarkupLineItems = [...bulkPricingMarkupLineItems];

      updatedBulkPricingMarkupLineItems[index] = { ...bulkPricingMarkupLineItems[index], ...updatedItem };

      setBulkPricingMarkupLineItems(updatedBulkPricingMarkupLineItems);

      onBulkPricingMarkupLineItemFieldChange(updatedBulkPricingMarkupLineItems);
    },
    [bulkPricingMarkupLineItems, onBulkPricingMarkupLineItemFieldChange],
  );

  const addNewItem = useCallback(() => {
    setProductItems(
      [...productItems, {
        id: 0,
        itemId: 0,
        productId: 0,
        quantity: 0
      }]);
  }, [productItems]);

  const removeItem = useCallback((indexToRemove: number) => {
    const updatedItems = productItems.filter((_, index) => index !== indexToRemove);
    setProductItems(updatedItems);
  }, [productItems]);

  const addNewbulkPricingMarkupLineItem = useCallback(() => {
    setBulkPricingMarkupLineItems(
      [...bulkPricingMarkupLineItems, {
        id: 0,
        productId: 0,
        pounds: 0,
        markup: 0
      }]);
  }, [bulkPricingMarkupLineItems]);

  const removeNewbulkPricingMarkupLineItem = useCallback((indexToRemove: number) => {
    const updatedItems = bulkPricingMarkupLineItems.filter((_, index) => index !== indexToRemove);
    setBulkPricingMarkupLineItems(updatedItems);
  }, [bulkPricingMarkupLineItems]);

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset(formValues);
    }
  }, [formValues, onReset]);

  const handleBack = useCallback(() => {
    navigate(backButtonPath ?? '/dashboard/products');
  }, [navigate, backButtonPath]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 4, sm: 4, md: 3, lg: 2 }}>
            <TextField
              value={formValues.name ?? ''}
              onChange={handleTextFieldChange}
              name="name"
              label="Name*"
              error={!!formErrors.name}
              helperText={formErrors.name ?? ' '}
              slotProps={{
                inputLabel: {
                  shrink: true
                }
              }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 4, md: 3, lg: 2 }}>
            <TextField
              value={formValues.sku ?? ''}
              onChange={handleTextFieldChange}
              name="sku"
              label="SKU"
              slotProps={{
                inputLabel: {
                  shrink: true
                }
              }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 4, md: 3, lg: 2 }}>
            <Autocomplete
              getOptionLabel={(option) => option.name}
              options={customersData?.customers || []}
              value={formValues?.customer}
              onChange={(_, newValue: Customer | null) => {
                handleFormFieldChange(
                  'customerId',
                  +(newValue?.id || 0)
                );
              }}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Customer"
                  error={!!formErrors.customerId}
                  helperText={(formErrors.customerId) ?? ' '}
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                />
              )}
              renderOption={(props, item) => {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    {...optionProps}
                  >
                    {item.name}
                  </Box>
                );
              }}
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 4, md: 3, lg: 2 }}>
            <TextField
              select
              value={String(formValues?.costingType ?? '0')}
              onChange={handleTextFieldChange}
              label="Costing Type"
              name="costingType"
              error={!!formErrors.costingType}
              fullWidth
              required
            >
              <MenuItem value={CostingType.STANDARD}>Standard</MenuItem>
              <MenuItem value={CostingType.BULK}>Bulk</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 4, sm: 4, md: 3, lg: 2 }}>
            <Autocomplete
              getOptionLabel={(option) => option.name}
              options={productsData?.products || []}
              value={formValues.matchingProduct}
              onChange={(_, newValue: Product | null) => {
                handleMatchingProductFieldChange(
                  +(newValue?.id || 0)
                );
              }}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Matching Product"
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    }
                  }}
                />
              )}
              renderOption={(props, item) => {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    {...optionProps}
                  >
                    {item.name}
                  </Box>
                );
              }}
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 4, md: 3, lg: 2 }}>
            <FormControlLabel
              sx={{ display: 'block', paddingTop: '.5em' }}
              control={
                <Switch
                  checked={formValues.hasInventory}
                  name="hasInventory"
                  onChange={handleCheckboxFieldChange}
                  slotProps={{
                    input: { 'aria-label': 'controlled' },
                  }} />}
              label="Has Inventory?" />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Stack spacing={2} sx={{ width: '100%' }}>
            <FormLabel sx={{ fontSize: 18 }} error={!!formErrors.items}>
              <strong>Ingredient Items</strong>
            </FormLabel>
            <div>
              <Button
                type="button"
                color="secondary"
                startIcon={<Add />}
                variant="outlined"
                onClick={addNewItem}>
                Add Item
              </Button>
            </div>
            {!!formErrors.items ?
              <FormHelperText error={!!formErrors.items}>
                {formErrors.items ?? ' '}
              </FormHelperText> : <></>
            }
          </Stack>
          <Box sx={{ maxHeight: 150, overflowY: 'auto', paddingTop: 2, paddingLeft: 1, width: '100%' }}>
            <Grid spacing={0} sx={{ width: '90%' }}>
              {productItems?.map((productItem: ProductItem, index: number) => (
                <Stack key={index} direction="row" spacing={2} justifyContent="space-between" sx={{ width: '100%' }}>
                  <ProductItemForm
                    items={itemsData?.items || []}
                    initialValue={productItem}
                    initialErrors={itemErrors.get(index)}
                    onFormValuesUpdate={(itemFormValues) => handleItemFormFieldChange(itemFormValues, index)}
                    showActionButtons={false} />
                  <Box sx={{ width: '36px' }}>
                    <IconButton
                      size="small"
                      style={{ marginTop: '8px' }}
                      onClick={() => removeItem(index)}>
                      <Delete htmlColor={red[400]} />
                    </IconButton>
                  </Box>
                </Stack>
              ))}
            </Grid>
          </Box>
        </Grid>
        {formValues?.costingType === CostingType.BULK && (
          <Grid container spacing={1} sx={{ mb: 2, width: '100%' }}>
            <Grid spacing={0} sx={{ mb: 2, width: '100%' }}>
              <Stack spacing={2} sx={{ width: '100%' }}>
                <FormLabel sx={{ fontSize: 18 }} error={!!formErrors.items}>
                  <strong>Bulk Pricing Markup Line Items</strong>
                </FormLabel>
                <div>
                  <Button
                    type="button"
                    color="secondary"
                    startIcon={<Add />}
                    variant="outlined"
                    onClick={addNewbulkPricingMarkupLineItem}>
                    Add Bulk Pricing Markup Line Item
                  </Button>
                </div>
                {!!formErrors.bulkPricingMarkupLineItems ?
                  <FormHelperText error={!!formErrors.bulkPricingMarkupLineItems}>
                    {formErrors.bulkPricingMarkupLineItems ?? ' '}
                  </FormHelperText> : <></>
                }
              </Stack>
            </Grid>
            <Box sx={{ maxHeight: 150, overflowY: 'auto', paddingTop: 2, paddingLeft: 1, width: '100%' }}>
              <Grid spacing={0} sx={{ width: '90%' }}>
                {bulkPricingMarkupLineItems?.map((bulkPricingMarkupLineItem: ProductBulkPricingMarkupLineItem, index: number) => (
                  <Stack key={index} direction="row" spacing={2} justifyContent="space-between" sx={{ width: '100%' }}>
                    <ProductBulkPricingMarkupLineItemForm
                      initialValue={bulkPricingMarkupLineItem}
                      initialErrors={bulkPricingMarkupLineItemErrors.get(index)}
                      onFormValuesUpdate={(bulkPricingMarkupLineItemFormValues) => handleBulkPricingMarkupLineItemFormFieldChange(bulkPricingMarkupLineItemFormValues, index)} />
                    <Box sx={{ width: '36px' }}>
                      <IconButton
                        size="small"
                        style={{ marginTop: '8px' }}
                        onClick={() => removeNewbulkPricingMarkupLineItem(index)}>
                        <Delete htmlColor={red[400]} />
                      </IconButton>
                    </Box>
                  </Stack>
                ))}
              </Grid>
            </Box>
          </Grid>
        )}
      </FormGroup>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Box sx={{ pt: 1.25 }}>
            Total (lbs): {productItems?.map((productItem: ProductItem) => productItem.quantity)?.reduce((a, b) => a + b, 0).toFixed(3)}
          </Box>
          <Button
            type="submit"
            variant="contained"
            size="large"
            color="success"
            startIcon={<SaveIcon />}
            loading={isSubmitting}
          >
            {submitButtonLabel}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
